import { Component, inject, OnInit } from '@angular/core';
import { SbDateTime, SbLocalDate } from '../../../services/api/api-date-time';
import { DateTime } from 'luxon';
import { PivotReport } from '../../../other/pivot/PivotReport';
import { API, CampaignReportDTO, CullDTO, Pagination } from '../../../services/api/api';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { UtilService } from '../../../services/util.service';
import { Pivot } from '../../../other/pivot/PivotConfig';
import { ErrorService } from '../../../services/error.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { animate, style, transition, trigger } from '@angular/animations';
import { marker } from '@biesbjerg/ngx-translate-extract-marker';
import { QueryParamService } from 'src/app/services/lists/query-params';
import {
  FieldSelection,
  MsOptions,
  SbBooleanToggleModel,
  ShareOfVoiceReportingElement,
} from '../../../services/global/global-interfaces';
import { PermissionService } from '../../../services/permission.service';
import { StringTransformationService } from '../../../services/transformation-helper/string-transformation.service';

@Component({
  selector: 'sb-page-share-of-voice',
  templateUrl: './sb-share-of-voice.component.html',
  styleUrls: ['./sb-share-of-voice.component.scss'],
  animations: [
    trigger('slideInOut', [
      transition(':enter', [style({ transform: 'translateY(-100%)' }), animate('200ms ease-in', style({ transform: 'translateY(0%)' }))]),
      transition(':leave', [animate('200ms ease-in', style({ transform: 'translateY(-100%)' }))]),
    ]),
  ],
  standalone: false,
})
export class SbShareOfVoiceComponent implements OnInit {
  private readonly stringTransformation = inject(StringTransformationService);

  msCullOptions: MsOptions;
  culls: CullDTO[];
  worker: Worker;

  debounceTimer: ReturnType<typeof setTimeout>;
  loading: boolean;
  noDataFound: boolean;
  _report: PivotReport;
  _reportData: CampaignReportDTO;
  _reportingObj: ShareOfVoiceReportingElement;
  _overviewData: any[];
  _additionalMediumGroupData: any[];
  _flatReportData: any[];
  resolveFields: boolean;
  reloadAllowed: boolean;
  filteredCulls: CullDTO[];

  elementsPagination: BehaviorSubject<Pagination>;
  collectionSizeElements: number;
  maxSizeElements: number;
  resultCountRole: number;

  _pageElements: number;
  paginationElementsInitialized = false;

  selectedField: FieldSelection;
  eleId: number;

  distinctElementSelection: string[]; // set fields in constructor

  fieldsToSelect: FieldSelection[];

  queryParamsParsed: boolean;

  initialBooleans: SbBooleanToggleModel[];

  playoutsNumber: boolean;

  initLoad: boolean;

  _from?: SbLocalDate;
  _till?: SbLocalDate;

  constructor(
    private api: API,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private queryParamService: QueryParamService,
    private errorHandler: ErrorService,
    public translate: TranslateService,
    public util: UtilService,
    public permissionService: PermissionService
  ) {
    this.debounceTimer = null;
    // this.loading = true;
    this.initLoad = true;
    this.noDataFound = false;
    this._overviewData = [];
    this._additionalMediumGroupData = [];
    this._flatReportData = [];
    this.resolveFields = false;
    this.reloadAllowed = true;
    this.filteredCulls = [];
    this.collectionSizeElements = -1;
    this.maxSizeElements = 20;
    this.resultCountRole = -1;
    this._pageElements = 1;
    this.paginationElementsInitialized = false;
    this.selectedField = undefined;
    this.eleId = null;
    this.distinctElementSelection = [
      'campaign',
      'commercial',
      'requirement',
      'booking',
      'mediumGroup',
      'medium',
      'product',
      'productCategory',
    ];
    this.fieldsToSelect = [];
    this.queryParamsParsed = false;
    this.initialBooleans = [];
    this.playoutsNumber = true;
    this.elementsPagination = new BehaviorSubject<Pagination>({
      page: this.pageElements,
      count: this.maxSizeElements ? this.maxSizeElements : 8,
      pages: -1,
      resultcount: -1,
    });
    this.culls = [];
  }

  get pageElements(): number {
    return this._pageElements;
  }

  get flatReportData(): any[] {
    return this._flatReportData;
  }

  set flatReportData(flatReportData: any[]) {
    this._flatReportData = flatReportData;
  }

  set overviewData(val) {
    this._overviewData = val;
  }

  get overviewData() {
    return this._overviewData;
  }

  set additionalMediumGroupData(val) {
    this._additionalMediumGroupData = val;
  }

  get additionalMediumGroupData() {
    return this._additionalMediumGroupData;
  }

  set reportingObj(val) {
    this._reportingObj = val;
  }

  get reportingObj(): any {
    return this._reportingObj;
  }

  get reportData(): CampaignReportDTO {
    return this._reportData;
  }

  set reportData(reportData: CampaignReportDTO) {
    this._reportData = reportData;
    this.reportingObj = { distinctElements: undefined, report: {}, selectedElement: undefined, overview: undefined };
    this.runWebWorker();
  }

  get report(): PivotReport {
    return this._report;
  }

  set report(report: PivotReport) {
    this._report = report;
  }

  get from(): SbLocalDate {
    return this._from;
  }

  set from(val) {
    this._from = val;
  }

  get till(): SbLocalDate {
    return this._till;
  }

  set till(val) {
    this._till = val;
  }

  setReportingObj() {
    this.reportingObj = null;
    for (const ele of this.overviewData) {
      if (ele.element.id === this.eleId) {
        this.reportingObj = ele;
        this.loading = false;
      }
    }
  }

  ngOnInit() {
    this.initBoolToggles();
    this.translate.get(marker('lang.globals.mediumGroup')).subscribe(mediumGroup => {
      this.translate.get(marker('lang.globals.medias')).subscribe(medias => {
        this.translate.get(marker('lang.globals.product')).subscribe(product => {
          this.translate.get(marker('lang.globals.productCategory')).subscribe(productCategory => {
            this.fieldsToSelect = [
              {
                name: mediumGroup,
                id: 'mediumGroup',
              },
              {
                name: medias,
                id: 'medium',
              },
              {
                name: product,
                id: 'product',
              },
              {
                name: productCategory,
                id: 'productCategory',
              },
            ];
            this.selectedField = this.fieldsToSelect[0];
            this.from = new SbLocalDate(DateTime.local().startOf('day').minus({ day: 15 }));
            // this.from = new SbLocalDate({year: 2017, month: 1, day: 1});
            this.till = new SbLocalDate(DateTime.local().startOf('day').minus({ day: 1 }));

            this.initElementsPagination();
            this.parseQueryParams();
          });
        });
      });
    });
  }

  initBoolToggles() {
    this.playoutsNumber = true;
    this.refreshBoolToggles();
  }

  refreshBoolToggles() {
    this.initialBooleans = [];
    this.initialBooleans = [
      {
        id: 'playoutTime',
        name: marker('lang.globals.playouts'),
        value: this.playoutsNumber,
        class: 'playouts',
        classPreview: 'green',
      },
    ];
  }

  selectElement($event) {
    if ($event) {
      this.loading = true;
      this.queryParamService.setQueryParam('id', $event);
      this.eleId = $event;
      this.setReportingObj();
    } else {
      this.queryParamService.clearQueryParam('id');
      this.eleId = null;
    }
  }

  initElementsPagination() {
    this.elementsPagination.subscribe((e: Pagination) => {
      this.collectionSizeElements = e.resultcount;
      this.maxSizeElements = e.count;
      this.resultCountRole = e.resultcount;
    });

    // this.getElementsFromField();
  }

  refreshQueryParams() {
    if (this.from) {
      this.queryParamService.setQueryParam('from', this.from.toString());
    } else {
      this.queryParamService.clearQueryParam('from');
    }
    if (this.till) {
      this.queryParamService.setQueryParam('till', this.till.toString());
    } else {
      this.queryParamService.clearQueryParam('till');
    }
    if (this.selectedField) {
      this.queryParamService.setQueryParam('field', this.selectedField.id);
    } else {
      this.queryParamService.clearQueryParam('field');
    }
    if (this.eleId) {
      this.queryParamService.setQueryParam('id', '' + this.eleId);
    } else {
      this.queryParamService.clearQueryParam('id');
    }
  }

  selectEle(ele) {
    this.queryParamService.setQueryParam('id', ele.id);
    this.refresh();
  }

  selectField(field) {
    this.loading = true;
    this.selectedField = field;
    this.queryParamService.setQueryParam('field', field.id);
    this.refresh();
    // this.getElementsFromField();
  }

  refreshBoolQueryParam(name: string, value: boolean) {
    if (value === true || value === false) {
      this.queryParamService.setQueryParam(name, '' + value);
    } else {
      this.queryParamService.clearQueryParam(name);
    }
  }

  playoutSwitch() {
    this.playoutsNumber = !this.playoutsNumber;
    // this.loading = true;
    // this.requestDebounced();
  }

  parseQueryParams() {
    this.parseDateParams();
    this.parseFieldParams();
    this.parseIdParams();
    if (this.selectedField && this.overviewData.length === 0 && this.reloadAllowed) {
      // this.reloadAllowed = false;
      // this.refresh();
    } else {
      if (this.reloadAllowed) {
        this.queryParamService.setQueryParam('field', 'mediumGroup');
        this.queryParamService.setQueryParam('from', this.from.toString());
        this.queryParamService.setQueryParam('till', this.till.toString());
      }
    }
  }

  parseDateParams() {
    if (this.queryParamService.getQueryParam('from') && this.queryParamService.getQueryParam('till')) {
      this.from = new SbLocalDate(DateTime.fromISO(this.queryParamService.getQueryParam('from')));
      this.till = new SbLocalDate(DateTime.fromISO(this.queryParamService.getQueryParam('till')));
    }
  }

  parseFieldParams() {
    const fieldToResolve = this.queryParamService.getQueryParam('field');
    for (const field of this.fieldsToSelect) {
      if (field.id === fieldToResolve) {
        this.selectedField = field;
      }
    }
  }

  parseIdParams() {
    this.eleId = this.queryParamService.getQueryParam('id');
  }

  refresh() {
    this.requestDebounced();
  }

  requestDebounced() {
    if (this.queryParamsParsed) {
      this.refreshQueryParams();
    }
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    this.debounceTimer = setTimeout(() => {
      this.refreshFromServer();
    }, 500);
  }

  refreshFromServer() {
    this.noDataFound = false;
    this.loading = true;
    let obs: Observable<any>;
    const queryParams = {
      // mediumGroup: [60],
      from: this.from ? this.from : null,
      till: this.till ? this.till : null,
      playerCull: this.util.arrayToIdArray(this.filteredCulls),
    };
    if (this.activatedRoute.snapshot.toString().indexOf('publisher-share-of-voice') !== -1) {
      obs = this.api.ss_nm_Reporting_executionReport(queryParams);
    } else {
      obs = this.api.ds_cm_Reporting_campaignReport(queryParams);
    }
    obs.subscribe(
      (campaignReport: CampaignReportDTO) => {
        this.reportData = campaignReport;
        // this.loading = false;
        if (this.initLoad) {
          this.initLoad = false;
        }
      },
      error => {
        if (error.error.status === 400 && error.error.message === 'no campaign report data found') {
          this.noDataFound = true;
          this.loading = false;
        } else {
          this.loading = false;
          this.errorHandler.errorHandler(error);
        }
      }
    );
  }

  getReportingElement(selectedKey, selectedId: number): Promise<any> {
    return new Promise<any>((res, rej) => {
      if (this.reportData) {
        let found: boolean;
        for (const report of this.reportData.data) {
          const ele = report[selectedKey];
          if (selectedId === ele) {
            found = true;
            res(this.getMappedObject(this.reportData, selectedKey, selectedId));
          }
        }
        if (!found) {
          rej();
        }
      }
    });
  }

  makeArray(data) {
    const tempObj = {};
    for (const [key, value] of Object.entries(data)) {
      tempObj[key] = [];
      for (const [k, val] of Object.entries(value)) {
        tempObj[key].push(val);
      }
    }
    return tempObj;
  }

  findDistinctFields(rawData) {
    const rawDataFields = {};
    for (const field of this.distinctElementSelection) {
      const keyObj = {};
      for (const row of rawData) {
        let fieldVal = row[field];
        fieldVal = JSON.stringify(fieldVal);
        keyObj[fieldVal] = true;
      }
      rawDataFields[field] = Array.from(Object.keys(keyObj));
    }
    return rawDataFields;
  }

  findDistinctFieldsAsDTO(report, rawData) {
    const rawDataFields = this.findDistinctFields(rawData);
    const dtoFields = {};
    for (const field of Object.keys(rawDataFields)) {
      const keyObj = {};
      for (const element of rawDataFields[field]) {
        let mappedObject = this.getMappedObject(report, field, element);
        if (mappedObject === undefined) {
          mappedObject = Pivot.EMPTY_VALUE;
        }
        keyObj[element] = mappedObject;
      }
      dtoFields[field] = keyObj;
    }
    return dtoFields;
  }

  flattenReportData(reportData: CampaignReportDTO) {
    if (!reportData) {
      return null;
    }

    const flat: any[] = [];
    reportData.data.forEach(campaignReportEntry => {
      campaignReportEntry.data.forEach(campaignReportData => {
        for (let hour = 0; hour < 24; hour++) {
          if (!campaignReportEntry.products || campaignReportEntry.products.length === 0) {
            campaignReportEntry.products = [];
            campaignReportEntry.products.push(Pivot.EMPTY_VALUE);
          }
          if (!campaignReportEntry.productCategories || campaignReportEntry.productCategories.length === 0) {
            campaignReportEntry.productCategories = [];
            campaignReportEntry.productCategories.push(Pivot.EMPTY_VALUE);
          }
          const factor = campaignReportEntry.products.length * campaignReportEntry.productCategories.length;
          for (const product of campaignReportEntry.products) {
            for (const productCategory of campaignReportEntry.productCategories) {
              const element: any = {};
              element.customer = campaignReportEntry.customer;
              element.campaign = campaignReportEntry.campaign;
              element.campaignType = campaignReportEntry.campaignType;
              element.commercial = campaignReportEntry.commercial;
              element.requirement = campaignReportEntry.requirement;
              element.booking = campaignReportEntry.booking;
              element.medium = campaignReportEntry.medium;
              element.mediumGroup = campaignReportEntry.mediumGroup;
              element.publisher = campaignReportEntry.publisher;
              element.channel = campaignReportEntry.channel;
              element.marker = campaignReportEntry.marker;
              element.product = product;
              element.productCategory = productCategory;

              element.date = campaignReportData.date.toString(); // .value.toString();
              element.screens = campaignReportData.totalScreens;
              element.hour = hour;
              element.playouts = (campaignReportData.hoursPlayouts[hour] as number) / factor;
              element.playbacktime = (campaignReportData.hoursPlaybacktime[hour] as number) / factor;
              element.visitors = (campaignReportData.hoursVisitors[hour] as number) / factor;
              element.uniqueVisitors = (campaignReportData.hoursVisitors[hour] as number) / factor;
              element.impressions = (campaignReportData.hoursImpressions[hour] as number) / factor;
              flat.push(element);
            }
          }
        }
      });
    });
    if (this.resolveFields) {
      return this.resolveFieldsFunction(flat);
    }
    return flat;
  }

  resolveFieldsFunction(data: any[]) {
    if (data && this.reportData) {
      const resolved = [];
      data.forEach(element => {
        resolved.push(this.resolveElement(this.reportData, element));
      });
      return resolved;
    } else {
      return data;
    }
  }

  resolveElement(report: CampaignReportDTO, element: any): any {
    if (!element) {
      return element;
    }
    const resolved = {};
    for (const key in element) {
      if (key) {
        let value: any = element[key];

        const mappedObject = this.getMappedObject(report, key, value);
        if (mappedObject) {
          let composed = '';
          if (mappedObject.title) {
            composed += mappedObject.title;
          }
          if (mappedObject.name) {
            composed += mappedObject.name;
          }
          if (mappedObject.id) {
            composed += ' [' + mappedObject.id + ']';
          }
          value = composed.trim();
        }
        resolved[key] = value;
      }
    }
    return resolved;
  }

  getMappedObject(report, dataKey, value) {
    let collectionObject = null;
    if (dataKey === 'advertiser' || dataKey === 'publisher') {
      collectionObject = report.clients;
    } else if (dataKey === 'customer') {
      collectionObject = report.customers;
    } else if (dataKey === 'campaign') {
      collectionObject = report.campaigns;
    } else if (dataKey === 'campaignType') {
      collectionObject = report.campaignTypes;
    } else if (dataKey === 'commercial') {
      collectionObject = report.commercials;
    } else if (dataKey === 'requirement') {
      collectionObject = report.requirements;
    } else if (dataKey === 'booking') {
      collectionObject = report.bookings;
    } else if (dataKey === 'medium') {
      collectionObject = report.media;
    } else if (dataKey === 'mediumGroup') {
      collectionObject = report.mediumGroups;
    } else if (dataKey === 'product') {
      collectionObject = report.products;
    } else if (dataKey === 'productCategory') {
      collectionObject = report.productCategories;
    } else if (dataKey === 'channel') {
      collectionObject = report.channels;
    } else if (dataKey === 'marker') {
      collectionObject = report.markers;
    }

    if (collectionObject) {
      return collectionObject[value];
    }
    return null;
  }

  resolveReportData(field: string, elementId: number, report: PivotReport) {
    const reportTemp = {
      aggregated: undefined,
      byDate: undefined,
      byHour: undefined,
      byTimeslot: undefined,
      totalNumber: undefined,
    };
    if (report) {
      reportTemp.aggregated = report.getSlicedData([{ column: { fieldName: field }, element: elementId }]);

      reportTemp.byDate = report.getSlicedDataBy([{ column: { fieldName: field }, element: elementId }], [{ row: { fieldName: 'date' } }]);

      reportTemp.byHour = report.getSlicedDataBy(
        [{ column: { fieldName: field }, element: elementId }],
        [{ row: { fieldName: 'date' } }, { row: { fieldName: 'hour' } }]
      );

      reportTemp.byTimeslot = report.getSlicedDataBy(
        [{ column: { fieldName: field }, element: elementId }],
        [{ row: { fieldName: 'hour' } }]
      );

      reportTemp.totalNumber = report.getSlicedData([]);

      return reportTemp;
    }
  }

  findDistinctElements(report, field: string, elementId: number) {
    if (report) {
      const slicedRawData = report.getSlicedRawData([{ column: { fieldName: field }, element: elementId }]);
      return this.makeArray(this.findDistinctFieldsAsDTO(this.reportData, slicedRawData));
    }
    return null;
  }

  runWebWorker() {
    if (typeof Worker !== 'undefined') {
      if (this.worker === undefined) {
        this.worker = new Worker(new URL('./sb-share-of-voice.worker', import.meta.url), {
          type: 'module',
        });
      }
      const tempObj = [];
      const addTempObj = [];
      let onlyMotiveObj = {
        element: undefined,
        report: undefined,
        distinctElements: undefined,
      };
      const flatReport = this.flattenReportData(this.reportData);
      this.worker.postMessage([flatReport, this.selectedField.id]);
      this.worker.onmessage = dataPOJO => {
        if (dataPOJO) {
          const data = PivotReport.loadPOJO(dataPOJO.data);
          const distinctFields: any = this.makeArray(this.findDistinctFieldsAsDTO(this.reportData, data.getRawData()));
          for (const rep of distinctFields[this.selectedField.id]) {
            if (rep) {
              const obj = {
                element: undefined,
                report: undefined,
                distinctElements: undefined,
              };
              const value = rep === Pivot.EMPTY_VALUE ? Pivot.EMPTY_VALUE : rep.id;
              obj.element = rep;
              obj.report = this.resolveReportData(this.selectedField.id, value, data);
              obj.distinctElements = this.findDistinctElements(data, this.selectedField.id, value);
              if (obj.element === Pivot.EMPTY_VALUE) {
                onlyMotiveObj = obj;
              } else {
                tempObj.push(obj);
              }
            }
          }
          if (onlyMotiveObj.element) {
            tempObj.push(onlyMotiveObj);
          }
          this.overviewData = tempObj;
          if (this.selectedField.id !== 'medium') {
            for (const rep of distinctFields.medium) {
              if (rep) {
                const obj = {
                  element: undefined,
                  report: undefined,
                  distinctElements: undefined,
                };
                const value = rep === Pivot.EMPTY_VALUE ? Pivot.EMPTY_VALUE : rep.id;
                obj.element = rep;
                obj.distinctElements = this.findDistinctElements(data, 'medium', value);
                if (obj.distinctElements[this.selectedField.id][0] === Pivot.EMPTY_VALUE) {
                  obj.report = this.resolveReportData('medium', value, data);
                  addTempObj.push(obj);
                }
              }
            }
            this.additionalMediumGroupData = addTempObj;
          } else {
            this.additionalMediumGroupData = [];
          }
          if (this.eleId) {
            this.eleId = Number(this.eleId);
            this.setReportingObj();
          } else {
            this.loading = false;
          }
        }
      };
    } else {
      console.log('Web Workers are not supported in this environment.');
    }
  }

  parseDate(date) {
    if (date && date.value) {
      return this.util.dateToDateString(date.value);
    }
  }

  handleDateChange(dates: { fromDate: SbDateTime; toDate: SbDateTime }) {
    this.from = new SbLocalDate(dates.fromDate.value);
    this.queryParamService.setQueryParam('from', this.from.toString());
    this.till = new SbLocalDate(dates.toDate.value);
    this.queryParamService.setQueryParam('till', this.till.toString());
  }

  update($event, type: string) {
    switch (type) {
      case 'PLAYER_CULLS':
        if ($event && $event?.length > 0) {
          this.filteredCulls = $event;
        } else {
          this.filteredCulls = [];
        }
        break;
    }
    // this.refresh();
  }

  msRequest(type: string, options?: MsOptions) {
    switch (type) {
      case 'PLAYER_CULLS':
        if (options) {
          this.msCullOptions = options;
        }
        if (this.msCullOptions && this.msCullOptions.pagination) {
          this.api
            .cs_Cull_retrieveAll(
              {
                search: this.stringTransformation.encodeSearchQueryParam(false, this.msCullOptions.search),
                type: 'PlayerCull',
              },
              this.msCullOptions.pagination
            )
            .subscribe(
              (culls: CullDTO[]) => {
                this.culls = culls;
              },
              error => {
                this.errorHandler.errorHandler(error);
              }
            );
        }
        break;
    }
  }
}
